import { UniqueIdGenerator } from '../utils/unique-id-generator';
import * as fb from '@/firebase';

export class ListItemEntry {
  public static fromItemId(itemId: string): ListItemEntry {
    const entry = new ListItemEntry();
    entry.itemId = itemId;
    return entry;
  }

  public static fromFirebaseDoc(document: any): ListItemEntry {
    const entry = new ListItemEntry();
    entry.itemId = document.itemId as string;
    return entry;
  }

  public itemId: string;

  public toFirebaseDoc() {
    return {
      itemId: this.itemId
    };
  }
}

export class ListModel {
  public static fromFirebaseDoc(id: string, document: any): ListModel {
    if (!document.authorId) {
      throw new Error('list model document missing authorId');
    }
    const itemEntries = document.itemEntries || [];

    let isPublic;
    if (document.isPublic === undefined) {
      // default old lists to public
      isPublic = true;
    } else {
      isPublic = document.isPublic;
    }

    const model = new ListModel({
      id,
      title: document.title as string,
      subtitle: document.subtitle as string,
      authorId: document.authorId as string,
      username: document.username as string,
      avatar: document.avatar as string,
      description: document.description as string,
      slug: document.slug as string,
      sections: document.sections as string[],
      isListed: document.isListed as boolean,
      dateCreated: new Date((document.dateCreated as number) || 0),
      timeLastUpdated: new Date((document.timeLastUpdated as number) || 0),
      tags: document.tags as string[],
      numSaves: (document.numSaves as number) || 0,
      color: document.color as string,
      itemEntries: itemEntries.map(ListItemEntry.fromFirebaseDoc),
      images: document.images as string[],
      isHighlighted: document.isHighlighted as boolean,
      isPublic
    });
    return model;
  }

  public static empty(): ListModel {
    return new ListModel({
      title: '',
      subtitle: '',
      authorId: '',
      id: '',
      slug: '',
      description: '',
      username: '',
      avatar: '',
      sections: [],
      isListed: true,
      dateCreated: new Date(),
      timeLastUpdated: new Date(),
      tags: [],
      numSaves: 0,
      color: 'coral',
      itemEntries: [],
      images: [],
      isHighlighted: false,
      isPublic: false
    });
  }

  public title: string;
  public subtitle: string;
  public authorId: string;
  public id: string;
  public slug: string;
  public description: string;
  public username: string;
  public avatar: string;
  public sections: string[];
  public isListed: boolean;
  public dateCreated: Date;
  public timeLastUpdated: Date;
  public tags: string[];
  public numSaves: number;
  public color: string;
  public itemEntries: ListItemEntry[];
  public images: string[];
  public isHighlighted: boolean;
  public isPublic: boolean;

  constructor({
    subtitle,
    title,
    authorId,
    id,
    slug,
    description,
    username,
    avatar,
    sections,
    isListed,
    dateCreated,
    timeLastUpdated,
    tags,
    numSaves,
    color,
    itemEntries,
    images,
    isHighlighted,
    isPublic
  }: {
    subtitle: string;
    title: string;
    authorId: string;
    id: string;
    slug: string;
    description: string;
    username: string;
    avatar: string;
    sections: string[];
    isListed: boolean;
    dateCreated: Date;
    timeLastUpdated: Date;
    tags: string[];
    numSaves: number;
    color: string;
    itemEntries: ListItemEntry[];
    images: string[];
    isHighlighted: boolean;
    isPublic: boolean;
  }) {
    this.title = title;
    this.subtitle = subtitle;
    this.authorId = authorId;
    this.id = id;
    this.slug = slug;
    this.description = description;
    this.username = username;
    this.avatar = avatar;
    this.sections = sections;
    this.isListed = isListed;
    this.dateCreated = dateCreated;
    this.timeLastUpdated = timeLastUpdated;
    this.tags = tags;
    this.numSaves = numSaves;
    this.color = color;
    this.itemEntries = itemEntries;
    this.images = images;
    this.isHighlighted = isHighlighted;
    this.isPublic = isPublic;
  }

  public toFirebaseDoc(): any {
    return {
      title: this.title,
      subtitle: this.subtitle || '',
      authorId: this.authorId,
      slug: this.slug,
      description: this.description || '',
      username: this.username,
      avatar: this.avatar,
      sections: this.sections,
      isListed: this.isListed,
      isPublic: this.isPublic,
      dateCreated: this.dateCreated.getTime(),
      timeLastUpdated: new Date().getTime(),
      tags: this.tags,
      numSaves: this.numSaves,
      color: this.color,
      itemEntries: this.itemEntries.map((i) => i.toFirebaseDoc()),
      images: this.images || [],
      isHighlighted: this.isHighlighted
    };
  }

  public get numItems(): number {
    return this.itemEntries.length;
  }

  public get link(): string {
    return '/l/' + this.username + '/' + this.slug;
  }

  public get editLink(): string {
    return '/l/' + this.username + '/' + this.slug + '/edit';
  }

  public get ownerLink(): string {
    return '/l/' + this.username + '/' + this.slug + '/owner';
  }

  public get bgColor(): string {
    const colorMap: any = {
      coral: '#ff7474',
      violet: '#8f7efa',
      teal: '#8bd4e4',
      tangerine: '#ffaa7a',
      periwinkle: '#8ab3f1',
      mint: '#58d5b7',
      highlighter: '#fdd264',
      pink: '#f794bd',
      gray: '#e3e8f0'
    };
    return colorMap[this.color] || colorMap.coral;
  }

  public setTimeLastUpdated() {
    this.timeLastUpdated = new Date();
  }

  public get firebaseConsoleLink() {
    return `https://console.firebase.google.com/project/${
      fb.firebaseConfig.projectId
    }/database/firestore/data~2F${fb.listsCollection.id}~2F${this.id}`;
  }

  public get isPrivate() {
    return !this.isPublic;
  }

  public get isDiscoverable() {
    return this.isPublic && this.isListed;
	}
	
	public get isUnlisted() {
		return this.isPublic && !this.isListed;
	}

  public setDiscoverable() {
    this.isPublic = true;
    this.isListed = true;
  }

  public setPrivate() {
    this.isPublic = false;
    this.isListed = false;
  }

  public setUnlisted() {
    this.isPublic = true;
    this.isListed = false;
	}
}
